import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, message, Popconfirm, Table, TableProps } from 'antd';
import { TableParams } from '../../../../model/request';
import { Tag } from '../../../../model/response';
import { deleteTag, getPageTag } from '../../../../service/tagService';
import Search from 'antd/es/input/Search';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import processError from '../../../../utils/processError';

function TagManagement() {
  const [fetching, setFetching] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const navigate = useNavigate();

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      defaultPageSize: 10,
    },
  });

  const handleTagPage: TableProps['onChange'] = (pagination, filters) => {
    setTableParams({
      pagination,
      filters,
    });
  };

  const fetchData = useCallback(() => {
    setFetching(true);
    getPageTag(tableParams, keyword)
      .then((tagPage) => {
        setTags(tagPage.content);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: tagPage.totalElements,
          },
        });
      })
      .catch((error) => console.error(error))
      .finally(() => setFetching(false));
  }, [keyword, tableParams]);

  useEffect(() => {
    if (tableParams.pagination) {
      setTableParams({
        pagination: {
          ...tableParams.pagination,
          current: 1,
        },
        filters: tableParams.filters,
      });
    }
    fetchData();
  }, [tableParams.pagination?.pageSize, keyword]);

  useEffect(() => {
    fetchData();
  }, [tableParams.pagination?.current]);

  const tagTableColumns: TableProps<Tag>['columns'] = [
    {
      title: 'STT',
      dataIndex: 'key',
      render: (_, tag, index) => <div>{index + 1}</div>,
    },
    {
      title: 'Tên thẻ',
      key: 'name',
      render: (_, tag) => <div className={'font-bold text-0B7BFF'}>{tag.name}</div>,
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, tag) => <Checkbox checked={tag.webViewActive} />,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
    },
    {
      title: 'Hành động',
      key: 'doTest',
      align: 'center',
      render: (_, tag) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={() => handleNavigateUpdateTag(tag)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa thẻ"
            description="Xác nhận xóa thẻ. Hành động không thể hoàn tác?"
            onConfirm={() => handleDeleteTag(tag)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined className={'text-white'} />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleDeleteTag = async (tag: Tag) => {
    try {
      await deleteTag(tag.id);
      fetchData();
      message.success('Xoá thẻ thành công');
    } catch (err) {
      processError(err);
    }
  };

  function handleNavigateUpdateTag(tag: Tag) {
    navigate(`/learning-assets/tags/update/${tag.id}`);
  }

  function handleNavigateAddTag() {
    navigate('/learning-assets/tags/create');
  }

  return (
    <div className={'w-full h-full p-4'}>
      <div className={'p-2 flex items-center justify-between bg-white rounded'}>
        <div className={'flex gap-4'}>
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={handleNavigateAddTag}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Search
          placeholder={'Nhập từ khóa tìm kiếm'}
          rootClassName={'h-9 rounded-3xl border border-solid border-f46055 flex items-center overflow-hidden w-fit'}
          onSearch={setKeyword}
        />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          dataSource={tags}
          columns={tagTableColumns}
          rowKey={(tag) => tag.id}
          onChange={handleTagPage}
          loading={fetching}
          pagination={tableParams.pagination}
        />
      </div>
    </div>
  );
}

export default TagManagement;
